import React, { useRef, useState, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import styled, { ThemeContext } from "styled-components"

import Reveal from "../utils/Reveal"

import Card from "../atoms/Card"
import Section from "../atoms/Section"
import FlexGrid from "../atoms/FlexGrid"
import Filter from "../atoms/Filter"
import Box from "../atoms/Box"
import Flex from "../atoms/Flex"
import ClearFiltersButton from "../atoms/ClearFiltersButton"

import AlphabeticalFilter from "../molecules/AlphabeticalFilter"
import FilteredGrid from "../molecules/FilteredGrid"
import TeamCard from "../molecules/TeamCard"
import SectionTitle from "../molecules/SectionTitle"
import SearchGrid from "../molecules/SearchGrid"
import SocialLink from "../utils/SocialLink"

const CarouselTrack = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  flex-wrap: nowrap;
  left: 0;
  align-items: center;
  scrollbar-width: none;

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const TeamGrid = ({
  section,
  sectionTitle,
  TGSectionTitle,
  flexGrid,
  TGFlexGrid,
  displayMode = "grid",
  carouselHeight = 200,
  carouselWidth = "normal",
  carouselSpacing = 12,
  useCard,
  card,
  TGCard,
  teamCards,
  imageShape,
  showNameOnHover,
  textPlacement,
  overlayStyle,
  overlayOpacity,
  zoomImageOnHover,
  align,
  socialLinkStyles,
  socialLinksLocation,
  socialLinksAlign,
  socialLinkSize,
  socialLinkSpacing,
  filter,
  TGFilter,
  filters,
  TGFilters,
  teamGridImageRatio,
  imageRatio,
  teamGridImageWidth,
  imageWidth,
  sort,
  TGSort,
  filterSortLocation,
  searchGrid,
  TGSearchGrid,
  alphabeticalFilter,
  gridInitialView,
  useSearchFilter,
  useAlphabeticalFilter,
  clearFiltersBtn,
}) => {
  clearFiltersBtn = {
    ...clearFiltersBtn,
    clearFiltersBtnText:
      clearFiltersBtn?.clearFiltersBtnText?.length > 0
        ? clearFiltersBtn?.clearFiltersBtnText
        : "Clear Filters",
  }

  sectionTitle = TGSectionTitle || sectionTitle
  flexGrid = TGFlexGrid || flexGrid
  card = TGCard || card
  filters = TGFilters || filters
  filter = TGFilter || filter
  sort = TGSort || sort
  imageRatio = teamGridImageRatio || imageRatio
  imageWidth = teamGridImageWidth || imageWidth
  displayMode = displayMode || "grid"
  carouselWidth = carouselWidth || "normal"
  carouselHeight = carouselHeight || 200
  carouselSpacing = carouselSpacing || 12
  searchGrid = { ...(TGSearchGrid || searchGrid), useSearch: useSearchFilter }
  alphabeticalFilter = {
    ...alphabeticalFilter,
    useFilter: useAlphabeticalFilter,
  }
  const searchProps = {
    ...searchGrid,
    useSearch: useSearchFilter,
    optionOneBoolField: "name",
    optionTwoBoolField: "positionTitle",
  }

  const isSearchFiltersOn = searchProps?.useSearch
  const isAlphaFiltersOn = alphabeticalFilter?.useFilter
  const isTagFilterOn = filters?.length > 0

  const shouldRenderClearFiltersButton =
    clearFiltersBtn.showClearFiltersBtn &&
    (isTagFilterOn || isSearchFiltersOn || isAlphaFiltersOn)

  const currentTheme = useContext(ThemeContext)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedAlphaLetterFilter, setSelectedAlphaLetterFilter] = useState("")
  const [filtersResetKey, setFiltersResetKey] = useState(0) //use to create unique key to force a remount of filter tags to ensure selected filters are reset when the clear button is clicked
  const filterPriority = searchGrid?.filterPriority ?? "searchAndFilters"

  const handleSearch = e => {
    const searchValue = e.target.value
    setSearchTerm(searchValue)
  }

  const handleClearFilters = () => {
    setSearchTerm("")
    setSelectedAlphaLetterFilter("")
    setFiltersResetKey(filtersResetKey + 1) //increment filtersResetKey with every clear reset to ensure unique filter for each filter tag instance
  }

  useEffect(() => {
    if (filterPriority === "searchOnly" && searchTerm.length > 0) {
      setSelectedAlphaLetterFilter("")
    }
  }, [searchTerm, filterPriority])

  const isCarousel = displayMode === "carousel"

  const getImageRatio = imageRatio => {
    switch (imageRatio) {
      case "sixteenNine":
        return 0.5625
      case "threeTwo":
        return 0.66
      case "fourThree":
        return 0.75
      case "oneOne":
        return 1
      case "threeFour":
        return 1.3333
      case "twoThree":
        return 1.5
      default:
        return 0.75
    }
  }

  // Click and drag functionality for carousel
  const carouselRef = useRef()
  let mouseDown = false
  let startX, scrollLeft

  const startDrag = e => {
    mouseDown = true
    startX = e.pageX - carouselRef.current.offsetLeft
    scrollLeft = carouselRef.current.scrollLeft
  }

  const endDrag = () => {
    mouseDown = false
  }

  const drag = e => {
    e.preventDefault()
    if (!mouseDown) return
    const x = e.pageX - carouselRef.current.offsetLeft
    const scroll = x - startX
    carouselRef.current.scrollLeft = scrollLeft - scroll
  }

  return (
    <Section {...section}>
      {sectionTitle && (
        <Reveal>
          <SectionTitle {...sectionTitle} mb={6} maxWidth={42} />
        </Reveal>
      )}

      {searchGrid?.useSearch && (
        <SearchGrid
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          alignment={searchGrid?.alignSearchBar}
          placeholder={searchGrid?.defaultSearchBarText || "Search"}
          themeColors={{
            themes: currentTheme?.themes,
            sectionTheme: section?.theme,
          }}
          marginBottom={
            clearFiltersBtn.showClearFiltersBtn && !isAlphaFiltersOn ? 4 : 0
          }
        />
      )}

      {alphabeticalFilter?.useFilter && (
        <AlphabeticalFilter
          selectedAlphaLetterFilter={selectedAlphaLetterFilter}
          setSelectedAlphaLetterFilter={setSelectedAlphaLetterFilter}
          marginBottom={
            filter?.layout === "tabs"
              ? 5
              : clearFiltersBtn.showClearFiltersBtn
                ? 6
                : 7
          }
          marginTop={
            clearFiltersBtn.showClearFiltersBtn && isSearchFiltersOn
              ? 4
              : isSearchFiltersOn
                ? 6
                : 0
          }
        />
      )}

      {isCarousel && (
        <Box
          position="relative"
          left={0}
          ml={
            carouselWidth === "fullWidth"
              ? {
                  _: "-24px",
                  lg: "-48px",
                  xl: "calc(50% - 50vw)",
                }
              : null
          }
          mr={
            carouselWidth === "fullWidth"
              ? {
                  _: "-24px",
                  lg: "-48px",
                  xl: "calc(50% - 50vw)",
                }
              : null
          }
        >
          <CarouselTrack
            ref={carouselRef}
            onMouseDown={startDrag}
            onMouseMove={drag}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
          >
            <Flex minWidth="fit-content" py="50px">
              {teamCards?.map((person, i) => (
                <Box
                  key={i}
                  position="relative"
                  minHeight="100%"
                  width={carouselHeight / getImageRatio(imageRatio)}
                  mr={
                    i === teamCards?.length - 1 && carouselWidth === "fullWidth"
                      ? {
                          _: "24px",
                          lg: "48px",
                          xl: `calc(50vw - ${section?.maxWidth / 2 - 16}px)`,
                        }
                      : i === teamCards?.length - 1 &&
                          carouselWidth !== "fullWidth"
                        ? null
                        : `${carouselSpacing}px`
                  }
                  ml={
                    i === 0 && carouselWidth === "fullWidth"
                      ? {
                          _: "24px",
                          lg: "48px",
                          xl: `calc(50vw - ${section?.maxWidth / 2 - 16}px)`,
                        }
                      : null
                  }
                  onDragStart={e => e.preventDefault()} //  ensuring the carousel scroll when panning or dragging images
                >
                  <TeamCard
                    key={i}
                    {...person}
                    useCard={useCard}
                    card={card}
                    align={align}
                    socialLinkStyles={socialLinkStyles}
                    socialLinksLocation={socialLinksLocation}
                    socialLinksAlign={socialLinksAlign}
                    socialLinkSize={socialLinkSize}
                    socialLinkSpacing={socialLinkSpacing}
                    imageShape={imageShape}
                    showNameOnHover={showNameOnHover}
                    textPlacement={textPlacement}
                    overlayStyle={overlayStyle}
                    overlayOpacity={overlayOpacity}
                    zoomImageOnHover={zoomImageOnHover}
                    imageRatio={imageRatio}
                    imageWidth={imageWidth}
                    imageFieldId={`${section.sectionId}.teamCards[${i}].image`}
                  />
                </Box>
              ))}
            </Flex>
          </CarouselTrack>
        </Box>
      )}

      {!isCarousel && (
        <FilteredGrid
          key={filtersResetKey}
          filtersResetKey={filtersResetKey}
          flexGrid={flexGrid}
          filters={filters}
          filtersStyles={{
            filterLocation: filter?.filterLocation,
            alignMenuItems: filter?.alignMenuItems,
            showFilterLabel: filter?.showFilterLabel,
            layout: filter?.layout,
            filterTextDisplay: filter?.filterTextDisplay,
            dropdownCard: filter?.dropdownCard,
            mb: 0,
          }} // only sending style fields to avoid possibly overwriting filter data
          card={{
            theme: section.theme,
            bg: section.bg,
            borderRadius: "md",
            // boxShadow: "md",
            boxShadow: "0 3px 10px rgb(0 0 0 / 30%)",
          }}
          sort={{
            ...sort,
            optionsObj: {
              customOrderBool: sort?.customOrderTitle || "Custom Order",
              optionOneBool: "First Name",
              optionTwoBool: "Last Name",
              optionThreeBool: "Position Title",
            },
          }}
          filterSortLocation={filterSortLocation}
          searchedTerm={searchTerm}
          selectedAlphaLetterFilter={selectedAlphaLetterFilter}
          setSearchedTerm={setSearchTerm}
          setSelectedAlphaLetterFilter={setSelectedAlphaLetterFilter}
          searchProps={searchProps}
          clearFiltersBtn={{
            ...clearFiltersBtn,
            showClearFiltersBtn: shouldRenderClearFiltersButton,
            handleClearFilters: handleClearFilters,
          }}
          alphabeticalFilter={alphabeticalFilter}
          gridInitialView={gridInitialView}
        >
          {teamCards &&
            teamCards.map((person, i) => (
              <TeamCard
                key={i}
                {...person}
                useCard={useCard}
                card={card}
                align={align}
                socialLinkStyles={socialLinkStyles}
                socialLinksLocation={socialLinksLocation}
                socialLinksAlign={socialLinksAlign}
                socialLinkSize={socialLinkSize}
                socialLinkSpacing={socialLinkSpacing}
                imageShape={imageShape}
                showNameOnHover={showNameOnHover}
                textPlacement={textPlacement}
                overlayStyle={overlayStyle}
                overlayOpacity={overlayOpacity}
                zoomImageOnHover={zoomImageOnHover}
                imageRatio={imageRatio}
                imageWidth={imageWidth}
                imageFieldId={`${section.sectionId}.teamCards[${i}].image`}
              />
            ))}
        </FilteredGrid>
      )}
    </Section>
  )
}

export default TeamGrid

TeamGrid.strapiProps = {
  strapi_id: PropTypes.number.isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  flexGrid: PropTypes.shape(FlexGrid.strapiProps),
  imageShape: PropTypes.oneOf(["default", "square", "circle"]),
  showNameOnHover: PropTypes.bool,
  textPlacement: PropTypes.oneOf(["belowImage", "onImage"]),
  overlayStyle: PropTypes.oneOf(["solid", "gradient"]),
  overlayOpacity: PropTypes.number,
  zoomImageOnHover: PropTypes.bool,
  useSearchFilter: PropTypes.bool,
  useAlphabeticalFilter: PropTypes.bool,
  alphabeticalFilter: PropTypes.shape(AlphabeticalFilter.strapiProps),
  gridInitialView: PropTypes.oneOf(["allItems", "empty"]),
  align: PropTypes.oneOf(["left", "center", "right"]),
  socialLinkStyles: PropTypes.shape(SocialLink.strapiProps),
  socialLinksLocation: PropTypes.oneOf(["inline", "below"]),
  socialLinksAlign: PropTypes.oneOf(["top", "middle", "bottom"]),
  socialLinkSize: PropTypes.number,
  socialLinkSpacing: PropTypes.number,
  filters: PropTypes.arrayOf(PropTypes.shape(Filter.strapiProps)),
  filter: PropTypes.shape(Filter.strapiProps),
  card: PropTypes.shape(Card.strapiProps),
  clearFiltersBtn: PropTypes.shape(ClearFiltersButton.strapiProps),
  teamCards: PropTypes.arrayOf(PropTypes.shape(TeamCard.strapiProps)),
}

TeamGrid.propTypes = {
  ...TeamGrid.strapiProps,
}

export const query = graphql`
  fragment TeamGrid on STRAPI__COMPONENT_SECTIONS_TEAM_GRID {
    strapi_id
    TEAMGRIDSection: section {
      ...Section
    }
    TGSectionTitle: sectionTitle {
      ...SectionTitle
    }
    TGFlexGrid: flexGrid {
      ...FlexGrid
    }
    displayMode
    carouselHeight
    carouselWidth
    carouselSpacing
    useCard
    TGCard: card {
      ...Card
    }
    teamCards {
      ...TeamCard
    }
    TGFilters: filters {
      ...Filter
    }
    TGFilter: filter {
      alignMenuItems
      filterLocation
      showFilterLabel
      layout
      filterTextDisplay
      dropdownCard {
        ...Card
      }
    }
    imageShape
    showNameOnHover
    textPlacement
    overlayStyle
    overlayOpacity
    zoomImageOnHover
    align
    teamGridImageRatio: imageRatio
    teamGridImageWidth: imageWidth
    TGSort: sort {
      ...Sort
    }
    useSearchFilter
    TGSearchGrid: searchGrid {
      ...SearchGrid
    }
    filterSortLocation
    useAlphabeticalFilter
    alphabeticalFilter {
      ...AlphabeticalFilter
    }
    gridInitialView
    clearFiltersBtn {
      ...ClearFiltersButton
    }
    socialLinksLocation
    socialLinksAlign
    socialLinkStyles {
      ...SocialLinkStyles
    }
    socialLinkSize
    socialLinkSpacing
  }
`
